import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@clerk/react";
import { Link, useSearchParams } from "react-router-dom";
import { Users, Play } from "lucide-react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import { makeApi } from "@/lib/api";

export default function Classes() {
  const { getToken } = useAuth();
  const api = useMemo(() => makeApi(getToken), [getToken]);
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get("code") || "");
  const [rooms, setRooms] = useState([]);
  const load = useCallback(() => api.get("/classrooms/joined").then((r) => setRooms(r.data)), [api]);
  useEffect(() => { load(); }, [load]);

  const join = async () => {
    if (!code.trim()) return;
    try { const r = await api.post("/classrooms/join", { code }); toast.success(`Joined ${r.data.name}!`); setCode(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not join"); }
  };

  return (
    <>
      <Nav />
      <main className="max-w-4xl mx-auto px-4 py-10">
        <h1 className="text-4xl font-black tracking-tight flex items-center gap-2"><Users size={32} strokeWidth={2.5} /> My classes</h1>
        <div className="sticker-card p-6 mt-6 flex gap-3 flex-wrap items-center" style={{ background: "#E6F7FF" }}>
          <p className="font-black w-full sm:w-auto">Got a class code from your teacher?</p>
          <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="ABC123" maxLength={8} data-testid="join-code-input"
            className="px-4 py-3 rounded-xl border-[3px] border-[#374151] font-black tracking-widest uppercase w-40 bg-white" />
          <button onClick={join} data-testid="join-class-btn"
            className="px-5 py-3 rounded-full border-[3px] border-[#374151] bg-[var(--tp-success)] text-white font-black shadow-[0_4px_0_0_#374151] active:translate-y-1 active:shadow-none">
            Join class
          </button>
        </div>

        <div className="mt-8 space-y-6" data-testid="joined-classes">
          {rooms.length === 0 && <p className="text-slate-500">You haven't joined any classes yet.</p>}
          {rooms.map((c) => (
            <div key={c.id} className="sticker-card p-6">
              <h2 className="text-2xl font-black">{c.name}</h2>
              <p className="text-slate-600">{c.description} • Teacher: {c.teacher_name || "—"}</p>
              <div className="mt-4 grid gap-3">
                {c.quizzes.length === 0 && <p className="text-slate-500 text-sm">No quizzes assigned yet.</p>}
                {c.quizzes.map((q) => (
                  <div key={q.id} className="flex items-center justify-between gap-3 border-[2px] border-[#374151] rounded-xl p-3">
                    <span className="font-black">{q.title}</span>
                    <Link to={`/quiz/${q.id}`} data-testid={`class-play-${q.id}`}
                      className="px-4 py-2 rounded-full border-[3px] border-[#374151] bg-[var(--tp-success)] text-white font-black text-sm inline-flex items-center gap-1 shadow-[0_3px_0_0_#374151]">
                      <Play size={14} strokeWidth={2.5} /> Play
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </main>
    </>
  );
}
